import { IControl } from "@trimblemaps/trimblemaps-js";
import * as DOM from '../../utils/dom';
import { fetchApi } from "services/api";
import type { Feature, Polygon } from "@trimblemaps/trimblemaps-js/geojson";
import "./TrimbleMapBookedControl.css";

export type VirtualTerminalControlOptions = {
  vehicleTypeId?: number;
};

class TrimbleMapVirtualTerminalControl implements IControl {
  _map: TrimbleMaps.Map;
  _container: HTMLElement;
  _button: HTMLButtonElement;
  _showing: boolean = false;
  options: VirtualTerminalControlOptions;

  constructor(options: VirtualTerminalControlOptions) {
    this.options = options;
    this.toggle = this.toggle.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
  }

  onAdd(map: TrimbleMaps.Map): HTMLElement {
    this._map = map;
    this._container = DOM.create('div', 'trimblemaps-ctrl trimblemaps-ctrl-group', map.getContainer());

    const button = this._button = DOM.create('button', 'trimblemaps-ctrl-icon trimblemaps-vt-button', this._container) as HTMLButtonElement;
    button.type = 'button';
    DOM.create('span', null, button).setAttribute('aria-hidden', 'true');
    if (this._button.firstElementChild) this._button.firstElementChild.setAttribute('title', 'Virtual Terminals');
    this._button.addEventListener('click', this.toggle);

    this._cleanUp();
    return this._container;
  }

  onRemove() {
    if (!this._container) return;
    this._cleanUp();
    this._container.remove();
    this._map = (undefined as any);
  }

  _cleanUp() {
    this._map.getLayer('terminal-polygons') && this._map.removeLayer('terminal-polygons');
    this._map.getSource('terminal-polygons') && this._map.removeSource('terminal-polygons');
  }

  toggle() {
    if (this._showing) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    if (this._showing) return;
    this._showing = true;
    if (this._button) this._button.classList.add('trimblemaps-vt-button-visible');
    this._onApply();
  }

  hide() {
    if (!this._showing) return;
    this._showing = false;
    if (this._button) this._button.classList.remove('trimblemaps-vt-button-visible');
    this._cleanUp();
  }

  _onApply() {
    if (!this._showing || !this.options.vehicleTypeId) return;

    let vehicleClass: number | undefined;
    if (this.options.vehicleTypeId === 1 || this.options.vehicleTypeId === 15) vehicleClass = 1;
    else if (this.options.vehicleTypeId === 2 || this.options.vehicleTypeId === 3) vehicleClass = 2;
    else if (this.options.vehicleTypeId === 4) vehicleClass = 4;
    else return;

    fetchApi(`/api/VirtualTerminal/List?vehicleClass=${vehicleClass}`)
      .then((response: { VirtualTerminals: Array<{ ID: number, Name: string, Polygon: { type: string, coordinates: number[][][] }, Stats?: { CalculatedStatus: string } }> }) => {
        const features: Feature<Polygon>[] = response.VirtualTerminals.map((terminal) => ({
          type: 'Feature',
          properties: { Status: terminal.Stats?.CalculatedStatus },
          geometry: {
            type: terminal.Polygon.type as "Polygon",
            coordinates: terminal.Polygon.coordinates
          }
        }));

        if (this._map.getLayer('terminal-polygons')) {
          this._map.removeLayer('terminal-polygons');
          this._map.removeSource('terminal-polygons');
        }

        this._map.addLayer({
          id: 'terminal-polygons',
          type: 'fill',
          source: {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features
            }
          },
          paint: {
            'fill-color': [
              'case',
              ['==', ['get', 'Status'], 'Surplus'], '#FF0000',
              ['==', ['get', 'Status'], 'Deficit'], '#008000',
              '#ffc107'
            ],
            'fill-opacity': 0.5
          }
        });
      });
  }
}

export default TrimbleMapVirtualTerminalControl;
